import { Flex, styled, Box, Button, InlineBanner } from '@cad-arc';
import {
    MapViewportEventView,
    MapViewportHospitalView,
    MapViewportUnitView,
    StationView,
    TimestampedGeoPoint,
} from '@mark43/cad-api';
import { NearbyGeoCautionDto } from '@mark43/rms-api';
import { useCallback, useMemo, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import { EsriMapFiltersAndLayersDropdowns } from './esri-map-filters-and-layers-dropdowns';
import { MapControls } from './map-controls';
import { MapTools } from './map-tools';
import { EsriToggleMapModeButton } from './esri-toggle-map-mode-button';
import { EsriBasemapSwitchButton } from './esri-basemap-switch-button';
import { SelectedEntityContextMenu } from './esri-map-selected-entity-context-menu';
import { useEsriBaseMap } from '../base-maps/use-esri-base-map';
import { FeatureLayer, reactiveUtils } from '../constructors';
import { useAmlFeatureLayer } from '../hooks/use-aml-feature-layer';
import { useAmlLocationHistoryFeatureLayer } from '../hooks/use-aml-location-history-feature-layer';
import { useAmlUncertaintyRadiusFeatureLayer } from '../hooks/use-aml-uncertainty-radius-feature-layer';
import { useBatchedHitTest } from '../hooks/use-batched-hit-test';
import { useEventFeatureLayer } from '../hooks/use-event-feature-layer';
import { useGeoPointLocationFeatureLayer } from '../hooks/use-geo-point-location-feature-layer';
import { useKeyboardShortcut } from '../hooks/use-keyboard-shortcut';
import { useMapControls } from '../hooks/use-map-controls';
import { usePhoneCallFeatureLayer } from '../hooks/use-phone-call-feature-layer';
import { usePolylineFeatureLayer } from '../hooks/use-polyline-feature-layer';
import { useStationFeatureLayer } from '../hooks/use-station-feature-layer';
import { useHospitalFeatureLayer } from '../hooks/use-hospital-feature-layer';
import { useSensorFeatureLayer } from '../hooks/use-sensor-feature-layer';
import type { MappableSensor } from '../../sensors/types';
import { useUnitFeatureLayer } from '../hooks/use-unit-feature-layer';
import type {
    EsriMapFilterOptions,
    EsriMapFilterState,
    DeprecatedEsriMapFilterOptions,
    DeprecatedEsriMapFilterState,
    GeoPointLocation,
    MapMode,
    MapOnChangeParams,
    MapView,
    MapViewpoint,
    MapViewportDerivedPolyline,
    PhoneCallGeoPointEntity,
    WebMap,
} from '../types';
import { logMapInfo } from '../helpers/utils';
import type { GeoPoint } from '../../location/types';
import {
    LayerId,
    EsriMapEntityTypeEnum,
    EsriMapContextMenuEntityTypeEnum,
    WebGLUnavailableReason,
} from '../types';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useNearbyGeoCautionsFeatureLayer } from '../hooks/use-nearby-geo-cautions-feature-layer';
import { useAddLayerClickHandler } from '../hooks/use-add-layer-click-handler';
import { useSensorGridCmdClick } from '../hooks/use-sensor-grid-cmd-click';
import { useInitialGeoPointPinFeatureLayer } from '../hooks/use-initial-geo-point-pin-feature-layer';
import type { AmlCallView } from '../../phone-calls/types';
import { reloadApp } from '../../common/helpers/utils';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY } from '../configurations';
import { FR_PROPERTIES_PANEL_WIDTH, PROPERTIES_PANEL_WIDTH } from '../constants';

// Esri map used in map window

/** What to tell the user when the map couldn't get WebGL2. A machine that had it earlier
 * in the session supports it; restarting the app is the fix, not IT. */
const WEBGL_UNAVAILABLE_BANNER: Record<WebGLUnavailableReason, { title: string; description: string }> = {
    'not-supported': {
        title: 'WebGL2 is required but not supported',
        description: 'Please contact IT to enable WebGL2 for this machine.',
    },
    'stopped-working': {
        title: 'Map graphics stopped working',
        description: 'Restart the app.',
    },
};

const EsriMapWrapper = styled(Flex)<{ $isMapLedgerOpen: boolean; $panelWidth: number }>`
    position: absolute;
    left: ${({ $isMapLedgerOpen, $panelWidth }) => ($isMapLedgerOpen ? `${$panelWidth}px` : 0)};
    right: 0;
    height: 100%;
    width: ${({ $isMapLedgerOpen, $panelWidth }) =>
        $isMapLedgerOpen ? `calc(100% - ${$panelWidth}px)` : '100%'};
    // added so that the map outline does not overlap the MapTopMenuBar component when focusing
    z-index: 0;
`;

type EsriMapProps = {
    amlCall?: AmlCallView;
    areRapidSOSAniAliLayersVisible: boolean;
    centerForUpdate?: GeoPoint;
    cleanupDelayedActions?: () => void;
    closeEntityContextMenu: () => void;
    clusterRadius?: number;
    contextMenuEntityId?: number;
    createEventAtLocationPin?: boolean;
    defaultCenter?: GeoPoint;
    defaultZoomLevel: number;
    deprecatedMapFilterOptions: DeprecatedEsriMapFilterOptions;
    deprecatedMapFilterState: DeprecatedEsriMapFilterState;
    dispatchFromMap?: boolean;
    embedded?: boolean;
    enableUnitClusteringOnMap?: boolean;
    entityViewZoomLevel: number;
    esriFeatureReductionClusteringEnabled?: boolean;
    filterOptions: EsriMapFilterOptions;
    filterState: EsriMapFilterState;
    initialGeoPoint?: GeoPoint;
    isAlternativeBasemap?: boolean;
    isFirstResponder: boolean;
    isFollowModeEnabled: boolean;
    isMapInactive?: boolean;
    isMapLedgerOpen: boolean;
    mapMode: MapMode;
    mapViewportEvents: MapViewportEventView[];
    mapViewportGeoPointLocations: GeoPointLocation[];
    mapViewportHospitals: MapViewportHospitalView[];
    mappableSensors: MappableSensor[];
    openSensorPanel: (sensorId: string | undefined) => void;
    mapViewportPolylines?: MapViewportDerivedPolyline[];
    mapViewportUnits: MapViewportUnitView[];
    mapViewRef: React.MutableRefObject<MapView | undefined>;
    moveMap: (center: GeoPoint) => void;
    nearbyGeoCautions: NearbyGeoCautionDto[];
    onMapChange: (params: MapOnChangeParams) => void;
    onMapLoadCompleted?: (webMap?: WebMap) => void;
    onMapUnmount?: () => void;
    /** Told where the map was, just before its view is torn down. */
    onViewTornDown?: (viewpoint: MapViewpoint) => void;
    openEventContextMenu: (eventId: number) => void;
    openUnitContextMenu: (unitId: number) => void;
    phoneCallGeoPointEntity?: PhoneCallGeoPointEntity;
    pinGeoPointOnMap?: (lat: number, lng: number) => void;
    renderEventContextMenuComponent: (eventId: number) => JSX.Element;
    renderUnitContextMenuComponent: (unitId: number) => JSX.Element;
    rollOutPersistWorkspaceMapFiltersEnabled?: boolean;
    saveFilters?: () => void;
    saveLayers?: () => void;
    selectAmlLocation?: (amlPhoneCallId: number) => void;
    selectCluster?: (clusterId: number) => void;
    selectEvent?: (eventId: number, shouldResetZoomLevel?: boolean) => void;
    selectedAmlPhoneNumberId?: number;
    selectedClusterId?: number;
    selectedContextMenuEntityType?: EsriMapContextMenuEntityTypeEnum;
    selectedEventId?: number;
    selectedEventLocation?: TimestampedGeoPoint;
    selectedGeoPointLocationId?: number;
    selectedHospitalId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    selectedNearbyGeoCautionCoordinates?: GeoPoint;
    selectedPhoneCallId?: number;
    selectedStationId?: number;
    selectedUnitId?: number;
    selectedUnitLocation?: TimestampedGeoPoint;
    selectedUnitUserId?: number;
    selectGeoPointLocation?: (geoPointLocationId: number) => void;
    selectHospital?: (hospitalId: number, shouldResetZoomLevel?: boolean) => void;
    selectPhoneCall?: (phoneCallId: number) => void;
    selectStation?: (stationId: number) => void;
    selectUnit?: (unitId: number, shouldResetZoomLevel?: boolean) => void;
    selectUnitUser?: (unitUserId: number) => void;
    setAssignedUnitForFollowMode?: () => void;
    setDeprecatedMapFilterState: (
        filterState: DeprecatedEsriMapFilterState,
        saveToLocalStorage?: boolean
    ) => void;
    setFilterState: (filterState: EsriMapFilterState) => void;
    setIsFollowModeEnabled: (iEnabled: boolean) => void;
    setMapLastInactiveTimestamp?: Dispatch<SetStateAction<number>>;
    setMapMode: (mapMode: MapMode) => void;
    setVisibleLayersIds: (layersId: LayerId[]) => void;
    setZoomForUpdate: (zoomLevel?: number) => void;
    shouldResetMapOnInitialLoad?: boolean;
    showMobileDevicesOnMap?: boolean;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
    stations: StationView[];
    theme: 'dark' | 'light';
    /** Build the map here rather than at its default center and zoom. */
    viewpointToRestore?: MapViewpoint;
    visibleLayersIds: LayerId[];
    webmapPortalId?: string;
    zoomForUpdate?: number;
};

export const EsriMap: React.FC<EsriMapProps> = ({
    mapViewRef,
    isAlternativeBasemap,
    webmapPortalId,
    embedded,
    esriFeatureReductionClusteringEnabled,
    enableUnitClusteringOnMap,
    clusterRadius,
    showMobileDevicesOnMap,
    shouldResetMapOnInitialLoad,
    showUnitLocationWhenGpsIsNotAvailable,
    dispatchFromMap,
    createEventAtLocationPin,

    theme,
    mapMode,
    setMapMode,

    defaultCenter,
    centerForUpdate,
    entityViewZoomLevel,
    defaultZoomLevel,
    zoomForUpdate,
    setZoomForUpdate,
    isMapLedgerOpen,
    isFirstResponder,
    isMapInactive,
    openEventContextMenu,
    openUnitContextMenu,
    closeEntityContextMenu,
    renderEventContextMenuComponent,
    renderUnitContextMenuComponent,
    isFollowModeEnabled,
    areRapidSOSAniAliLayersVisible,
    filterOptions,
    filterState,
    setFilterState,
    visibleLayersIds,
    setVisibleLayersIds,
    saveFilters,
    saveLayers,
    rollOutPersistWorkspaceMapFiltersEnabled,
    deprecatedMapFilterOptions,
    deprecatedMapFilterState,
    setDeprecatedMapFilterState,

    // data for rendering feature layers
    mapViewportEvents,
    mapViewportUnits,
    mapViewportGeoPointLocations,
    nearbyGeoCautions,
    mapViewportPolylines,
    mapViewportHospitals,
    mappableSensors,
    openSensorPanel,
    stations,
    phoneCallGeoPointEntity,
    amlCall,

    // selected entity on map
    selectedEventId,
    selectedUnitId,
    selectedUnitUserId,
    selectedStationId,
    selectedHospitalId,
    selectedGeoPointLocationId,
    selectedClusterId,
    selectedPhoneCallId,
    selectedAmlPhoneNumberId,
    selectedMapLedgerEntityType,
    selectedUnitLocation,
    selectedEventLocation,

    // selected entity type and entity id to show context menu
    selectedContextMenuEntityType,
    contextMenuEntityId,

    // focused nearby geo caution on map
    selectedNearbyGeoCautionCoordinates,

    // initial location of a pin by right-click on the map
    initialGeoPoint,

    // moveMap,
    onMapChange,
    setIsFollowModeEnabled,
    pinGeoPointOnMap,

    // select entity actions
    selectEvent,
    selectUnit,
    selectUnitUser,
    selectHospital,
    selectGeoPointLocation,
    selectStation,
    selectPhoneCall,
    selectAmlLocation,
    selectCluster,

    onMapLoadCompleted,
    onMapUnmount,
    cleanupDelayedActions,
    setMapLastInactiveTimestamp,

    setAssignedUnitForFollowMode,
    viewpointToRestore,
    onViewTornDown,
}) => {
    const mapPopupVisibilityListener = useRef(
        reactiveUtils.watch(
            () => undefined,
            () => undefined
        )
    );

    const {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        webGLUnavailable,
        isMapLoading,
        isMapLoadingFailed,
        setMapLoadFailed,
    } = useEsriBaseMap({
        zoom: defaultZoomLevel,
        mapOnChange: onMapChange,
        center: centerForUpdate || defaultCenter,
        isAlternativeBasemap,
        webmapPortalId,
        shouldResetMapOnInitialLoad,
        mapMode,
        viewpointToRestore,
        onViewTornDown,
    });

    if (mapViewRef && mapView) {
        mapViewRef.current = mapView;
    }

    const resetMapPopupVisibilityListener = useCallback(
        (shouldAvoidPopupOpening: boolean) => {
            if (!mapView) return;

            mapPopupVisibilityListener.current?.remove();
            mapPopupVisibilityListener.current = reactiveUtils.watch(
                () => mapView.popup.visible,
                () => {
                    if (shouldAvoidPopupOpening) {
                        mapView.popup.visible = false;
                    }
                }
            );
        },
        [mapView]
    );

    // the layers added first will be at bottom of the map.
    // last layer will be at top.

    const { stationsLayer } = useStationFeatureLayer({
        stations,
        selectedStationId,
        webMap,
        selectedMapLedgerEntityType,
    });

    const { hospitalsLayer } = useHospitalFeatureLayer({
        mapViewportHospitals,
        selectedHospitalId,
        webMap,
        selectedMapLedgerEntityType,
    });

    const { sensorsLayer } = useSensorFeatureLayer({
        mappableSensors,
        webMap,
    });

    const { unitsLayer, openUnitsClusterPopup } = useUnitFeatureLayer({
        mapViewportUnits,
        selectedUnitId,
        selectUnit,
        selectedUnitUserId,
        selectUnitUser,
        selectedClusterId,
        mapView,
        webMap,
        showMobileDevicesOnMap,
        enableUnitClusteringOnMap,
        clusterRadius,
        selectedMapLedgerEntityType,
        showUnitLocationWhenGpsIsNotAvailable,
        dispatchFromMap,
        openUnitContextMenu,
        isFirstResponder,
        mapMode,
    });
    const { eventsLayer } = useEventFeatureLayer({
        mapViewportEvents,
        zoom: entityViewZoomLevel,
        selectedEventId,
        webMap,
        selectedMapLedgerEntityType,
        mapMode,
    });

    useNearbyGeoCautionsFeatureLayer({
        nearbyGeoCautions,
        selectedNearbyGeoCautionCoordinates,
        webMap,
    });

    useInitialGeoPointPinFeatureLayer({
        initialGeoPoint,
        webMap,
    });

    const { geoPointLocationLayer } = useGeoPointLocationFeatureLayer({
        mapViewportGeoPointLocations,
        selectedGeoPointLocationId,
        webMap,
        selectedMapLedgerEntityType,
    });

    const { phoneCallLayer } = usePhoneCallFeatureLayer({
        phoneCallGeoPointEntity,
        webMap,
        selectedPhoneCallId,
        selectedMapLedgerEntityType,
        areRapidSOSAniAliLayersVisible,
    });

    // draw AML (RapidSOS) uncertainty circle first, then AML location history,
    // and finally AML current location (so current AML icon is at top of uncertainty circle).
    // Start of AML related layers
    useAmlUncertaintyRadiusFeatureLayer({
        amlCall,
        showAmlCallRadiusAndLedger: !!selectedAmlPhoneNumberId,
        webMap,
        hasClusters: esriFeatureReductionClusteringEnabled,
        areRapidSOSAniAliLayersVisible,
    });

    useAmlLocationHistoryFeatureLayer({
        amlCall,
        webMap,
        areRapidSOSAniAliLayersVisible,
    });

    const { amlLayer } = useAmlFeatureLayer({
        amlCall,
        webMap,
        selectedAmlPhoneNumberId,
        selectedMapLedgerEntityType,
        areRapidSOSAniAliLayersVisible,
    });
    // End of AML layers.

    usePolylineFeatureLayer({
        mapViewportPolylines,
        webMap,
    });

    useKeyboardShortcut({
        mapRef,
        mapView,
        webMap,
        defaultZoomLevel,
        entityViewZoomLevel,
        defaultCenter,
        isFollowModeEnabled,
        setIsFollowModeEnabled,
        selectedUnitLocation,
        selectedEventLocation,
    });

    const layers = useMemo(() => {
        return [
            stationsLayer,
            hospitalsLayer,
            sensorsLayer,
            eventsLayer,
            unitsLayer,
            phoneCallLayer,
            amlLayer,
        ].filter((l) => !!l) as FeatureLayer[];
    }, [
        stationsLayer,
        hospitalsLayer,
        sensorsLayer,
        eventsLayer,
        unitsLayer,
        phoneCallLayer,
        amlLayer,
    ]);

    useBatchedHitTest(mapView, layers, eventsLayer);

    const { focusEntity, zoomIn, zoomOut, resetMap, setZoomLevel } = useMapControls({
        mapView,
        defaultCenter,
        defaultZoomLevel,
        entityViewZoomLevel,
        isFollowModeEnabled,
        setIsFollowModeEnabled,
        setAssignedUnitForFollowMode,
    });
    const setMapZoomLevel = useCallback(
        (zoomLevel?: number) => {
            // set/update the map zoom level via useEffect in useMapControls
            setZoomLevel(zoomLevel);
            // clear zoomForUpdate so MapControls will not keep updating the zoom level.
            setZoomForUpdate(undefined);
        },
        [setZoomForUpdate, setZoomLevel]
    );

    useEffect(() => {
        return function cleanup() {
            if (mapViewRef.current) {
                mapViewRef.current = undefined;
            }
            cleanupDelayedActions?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapViewRef]);

    useEffect(
        function initialMapLoad() {
            if (!isMapLoading && unitsLayer && webMap) {
                unitsLayer.load().then(() => {
                    onMapLoadCompleted?.(webMap);
                });
            }

            return () => {
                onMapUnmount?.();
            };
        },
        [isMapLoading, webMap, onMapLoadCompleted, onMapUnmount, unitsLayer]
    );

    useEffect(
        function clearMapStateOnFailedLoading() {
            if (isMapLoadingFailed) {
                cleanupDelayedActions?.();
            }
        },
        [isMapLoadingFailed, cleanupDelayedActions]
    );

    // Cmd+click anywhere on the map opens the sensor grid for that point.
    useSensorGridCmdClick({ mapView });

    useAddLayerClickHandler({
        mapView,
        webMap,
        selectCluster,
        selectUnit,
        selectUnitUser,
        selectEvent,
        selectStation,
        selectHospital,
        openSensorPanel,
        selectGeoPointLocation,
        pinGeoPointOnMap,
        selectPhoneCall,
        selectAmlLocation,
        eventsLayer,
        unitsLayer,
        stationsLayer,
        hospitalsLayer,
        sensorsLayer,
        geoPointLocationLayer,
        phoneCallLayer,
        amlLayer,
        resetMapPopupVisibilityListener,
        hasClusters: esriFeatureReductionClusteringEnabled,
        openUnitsClusterPopup,
        dispatchFromMap,
        isFirstResponder,
        createEventAtLocationPin,
        openEventContextMenu,
        openUnitContextMenu,
    });

    useEffect(() => {
        if (isFirstResponder && mapRef?.current) {
            mapRef.current.style.visibility = isMapInactive ? 'hidden' : 'visible';
            const currentTimestamp = new Date().getTime();

            if (isMapInactive) {
                // when map tab changed from active to inactive (navigate to other tabs), set map inactive timestamp
                setMapLastInactiveTimestamp?.(currentTimestamp);
            } else {
                // when map tab changed from inactive to active (navigate from other tab to map). clear inactive timestamp.
                setMapLastInactiveTimestamp?.(0);
            }
        }
    }, [isFirstResponder, isMapInactive, setMapLastInactiveTimestamp, mapRef]);

    if (webGLUnavailable) {
        return (
            <InlineBanner
                status="error"
                title={WEBGL_UNAVAILABLE_BANNER[webGLUnavailable].title}
                description={WEBGL_UNAVAILABLE_BANNER[webGLUnavailable].description}
                style={{
                    position: 'absolute',
                    marginTop: '60px',
                    zIndex: 1,
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                }}
                data-testid="MAP_LOADING_ERROR_INFO_BANNER"
            />
        );
    }

    if (isMapLoadingFailed) {
        return (
            <InlineBanner
                status="error"
                title={errorOccurredWhile('loading the map').split('.')[0]}
                description={
                    <Box minWidth={500}>
                        <Button
                            onClick={() => {
                                storageHelpers.removeItem(
                                    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY
                                );
                                logMapInfo({
                                    source: 'user_action',
                                    event: 'retry_default_basemaps',
                                });
                                reloadApp();
                            }}
                        >
                            Try again with default basemaps
                        </Button>{' '}
                        or{' '}
                        <Button
                            onClick={() => {
                                storageHelpers.setItem(
                                    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                                    new Date().toISOString()
                                );
                                logMapInfo({
                                    source: 'user_action',
                                    event: 'switch_alternative_basemaps',
                                });
                                reloadApp();
                            }}
                        >
                            Switch to alternative basemaps
                        </Button>
                    </Box>
                }
                onClose={() => {
                    setMapLoadFailed?.(false);
                    logMapInfo({
                        source: 'user_action',
                        event: 'close_error_banner',
                    });
                }}
                style={{
                    position: 'absolute',
                    marginTop: '60px',
                    zIndex: 1,
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                }}
                data-testid="MAP_LOADING_ERROR_INFO_BANNER"
            />
        );
    }

    return (
        <EsriMapWrapper
            data-testid="ESRI_MAP_WRAPPER"
            $isMapLedgerOpen={!!isMapLedgerOpen}
            $panelWidth={isFirstResponder ? FR_PROPERTIES_PANEL_WIDTH : PROPERTIES_PANEL_WIDTH}
        >
            {mapComponent}
            <EsriMapFiltersAndLayersDropdowns
                webMap={webMap}
                isMapLoading={isMapLoading}
                visibleLayersIds={visibleLayersIds}
                filterOptions={filterOptions}
                filterState={filterState}
                setFilterState={setFilterState}
                setVisibleLayersIds={setVisibleLayersIds}
                saveFilters={saveFilters}
                saveLayers={saveLayers}
                rollOutPersistWorkspaceMapFiltersEnabled={rollOutPersistWorkspaceMapFiltersEnabled}
                deprecatedFilterOptions={deprecatedMapFilterOptions}
                deprecatedFilterState={deprecatedMapFilterState}
                setDeprecatedFilterState={setDeprecatedMapFilterState}
            />
            <MapControls
                zoomForUpdate={zoomForUpdate}
                focusEntity={focusEntity}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                resetMap={resetMap}
                setZoomLevel={setMapZoomLevel}
                isFollowModeEnabled={isFollowModeEnabled}
            />
            <MapTools mapView={mapView} />
            <EsriToggleMapModeButton
                theme={theme}
                mapMode={mapMode}
                setMapMode={setMapMode}
                mapView={mapView}
                embedded={embedded}
                setMapLoadFailed={setMapLoadFailed}
                isAlternativeBasemap={isAlternativeBasemap}
                isFirstResponder={isFirstResponder}
            />
            {isAlternativeBasemap && (
                <EsriBasemapSwitchButton
                    onClick={() => {
                        storageHelpers.removeItem(MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY);
                        logMapInfo({
                            source: 'user_action',
                            event: 'switch_default_basemaps',
                        });
                        reloadApp();
                    }}
                >
                    Switch to default basemaps
                </EsriBasemapSwitchButton>
            )}
            <SelectedEntityContextMenu
                mapRef={mapRef}
                selectedContextMenuEntityType={selectedContextMenuEntityType}
                contextMenuEntityId={contextMenuEntityId}
                closeEntityContextMenu={closeEntityContextMenu}
                renderEventContextMenuComponent={renderEventContextMenuComponent}
                renderUnitContextMenuComponent={renderUnitContextMenuComponent}
            />
        </EsriMapWrapper>
    );
};
